// =============================================================================
// DPC Dog Fit Index — inline capture form (T-549 Task 6)
// STAGED, NOT DEPLOYED. Not loaded by any live/test theme asset manifest.
// =============================================================================
// Ported from theme/live/assets/dpc-send-details.js (T-184). Kept: optimistic
// UI, honeypot, Turnstile (with the same graceful-degradation path), GA4
// event mirroring, localStorage retry queue with identical backoff schedule
// and in-flight guard (both fixed live 2026-05-13 after real incidents —
// see the T-184 file's comments for the two bugs those guards prevent).
// Dropped vs T-184: focus trap / backdrop / modal open-close (these are
// inline forms, not a modal), pet-name row management (no pet-name field
// on this form — see CAPTURE-FORM-SPEC.md for why).
//
// Multiple instances can exist on one page (one per `hook`), each with its
// own root `[data-dpc-fitindex-capture]` — this file wires ALL of them, not
// just the first, since the Fit Index page has up to 4 forms per the LOCKED
// "contextual section forms, ~1 per major section" decision.
// =============================================================================
(function () {
  'use strict';

  const roots = document.querySelectorAll('[data-dpc-fitindex-capture]');
  if (!roots.length) return;

  function isProductionHost() {
    try {
      if (window.Shopify && window.Shopify.theme && window.Shopify.theme.role && window.Shopify.theme.role !== 'main') {
        return false;
      }
    } catch (_) {}
    const h = (location.hostname || '').toLowerCase();
    if (/\.myshopify\.com$/.test(h) || /\.shopifypreview\.com$/.test(h)) return false;
    return true;
  }

  function uuid() {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  function nowIso() { return new Date().toISOString(); }

  const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
  const STORAGE_KEY = 'dpc_fitindex_queue';
  const BANNER_KEY = 'dpc_fitindex_save_failed';
  const BACKOFF_MS = [0, 5e3, 30e3, 2 * 60e3, 10 * 60e3, 30 * 60e3];
  const MAX_ATTEMPTS = BACKOFF_MS.length;
  const QUEUE_MAX_AGE_MS = 60 * 60 * 1000;

  let toastEl = null;
  function showToast(msg) {
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'dpc-toast';
      toastEl.setAttribute('role', 'status');
      toastEl.setAttribute('aria-live', 'polite');
      document.body.appendChild(toastEl);
    }
    toastEl.textContent = msg;
    toastEl.dataset.visible = 'true';
    setTimeout(function () { toastEl.dataset.visible = 'false'; }, 4500);
  }

  function queueRead() {
    try { const raw = localStorage.getItem(STORAGE_KEY); return raw ? JSON.parse(raw) : []; }
    catch (_) { return []; }
  }
  function queueWrite(items) {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(items)); } catch (_) {}
  }
  function queueAdd(payload) {
    let items = queueRead();
    items = items.filter(function (it) { return it.payload && it.payload.hook !== payload.hook; });
    if (items.length >= 20) items.shift();
    items.push({ payload: payload, attempts: 0, firstAttemptAt: Date.now(), nextAttemptAt: Date.now() });
    queueWrite(items);
  }
  function queueRemove(idempotency_key) {
    queueWrite(queueRead().filter(function (it) { return it.payload.idempotency_key !== idempotency_key; }));
  }

  let _submitInFlight = false;

  window.__dpcFitIndex = { uuid, nowIso, EMAIL_RE };

  roots.forEach(function (root) {
    const hook = root.dataset.hook || 'dog-fitindex-unknown';
    const form = root.querySelector('form[data-dpc-fitindex-form]');
    if (!form) return;

    const CONFIG = {
      submitEndpoint: root.dataset.submitEndpoint,
      turnstileSiteKey: root.dataset.turnstileSiteKey || '',
      consentVersion: root.dataset.consentVersion || 'v1.0-fitindex-2026-07-29',
      measurementId: root.dataset.ga4Id || 'G-8JF1NE04VM',
      apiSecret: root.dataset.ga4Secret,
      source: root.dataset.source || 'dog-content',
      pageHandle: root.dataset.pageHandle || 'the-dog-fit-index',
      hook: hook,
    };

    let _turnstileErrored = false;

    function getGa4ClientId() {
      try {
        const cookies = document.cookie.split(';');
        for (let i = 0; i < cookies.length; i++) {
          const c = cookies[i].trim();
          if (c.indexOf('_ga=') === 0) {
            const m = c.substring(3).match(/GA\d+\.\d+\.(\d+\.\d+)/);
            if (m && m[1]) return m[1];
          }
        }
      } catch (_) {}
      return Math.floor(Math.random() * 2147483647) + '.' + Math.floor(Date.now() / 1000);
    }

    function sendGa4(name, params) {
      try {
        if (!CONFIG.apiSecret || !isProductionHost()) return;
        const payload = {
          client_id: getGa4ClientId(),
          events: [{ name: name, params: Object.assign({ hook: CONFIG.hook, source: CONFIG.source }, params || {}) }],
        };
        const endpoint = 'https://www.google-analytics.com/mp/collect'
          + '?measurement_id=' + encodeURIComponent(CONFIG.measurementId)
          + '&api_secret=' + encodeURIComponent(CONFIG.apiSecret);
        fetch(endpoint, { method: 'POST', headers: { 'Content-Type': 'text/plain' }, body: JSON.stringify(payload), keepalive: true }).catch(function () {});
      } catch (_) {}
    }

    function readForm() {
      const fd = new FormData(form);
      return {
        email: (fd.get('email') || '').toString().trim(),
        consent: fd.get('consent') === 'on' || fd.get('consent') === 'true',
        honeypot: (fd.get('website') || '').toString(),
        turnstile_token: (fd.get('cf-turnstile-response') || '').toString(),
      };
    }

    function setFieldError(input, msg) {
      input.setAttribute('aria-invalid', 'true');
      let hint = input.parentNode.querySelector('.dpc-fitindex-capture__hint--error');
      if (!hint) {
        hint = document.createElement('p');
        hint.className = 'dpc-fitindex-capture__hint--error';
        hint.style.color = '#b00020';
        hint.style.fontSize = '12px';
        input.parentNode.appendChild(hint);
      }
      hint.textContent = msg;
    }
    function clearFieldError(input) {
      input.setAttribute('aria-invalid', 'false');
      const hint = input.parentNode.querySelector('.dpc-fitindex-capture__hint--error');
      if (hint) hint.remove();
    }
    function setConsentError(visible) {
      const msg = form.querySelector('[data-dpc-fitindex-consent-error]');
      if (msg) msg.hidden = !visible;
    }

    function validate(values) {
      let ok = true;
      const emailInput = form.querySelector('input[name="email"]');
      if (!values.email || !EMAIL_RE.test(values.email)) {
        setFieldError(emailInput, 'Please enter a valid email address.');
        ok = false;
      } else {
        clearFieldError(emailInput);
      }
      if (!values.consent) { setConsentError(true); ok = false; } else { setConsentError(false); }
      if (values.honeypot) ok = false;
      if (CONFIG.turnstileSiteKey && !values.turnstile_token && !_turnstileErrored) ok = false;
      return ok;
    }

    function buildPayload(values) {
      return {
        email: values.email,
        first_name: null,
        form_type: 'fit_index',
        pet_type: 'dog',
        source: CONFIG.source,
        hook: CONFIG.hook,
        page_handle: CONFIG.pageHandle,
        idempotency_key: uuid(),
        asset_token: uuid(),
        consent_timestamp_utc: nowIso(),
        consent_ip_address: '',
        consent_wording_version: CONFIG.consentVersion,
        consent_source_url: location.href,
        consent_age_affirmed: true,
        consent_marketing_purpose_disclosed: ['fit_index_guide', 'newsletter', 'product_updates'],
        turnstile_token: values.turnstile_token || '',
        turnstile_unavailable: _turnstileErrored,
      };
    }

    async function submitOnce(payload) {
      _submitInFlight = true;
      try {
        const resp = await fetch(CONFIG.submitEndpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
          keepalive: true,
          credentials: 'omit',
        });
        if (resp.status === 200) return 'ok';
        if (resp.status === 410) return 'gate';
        if (resp.status === 400) return 'fatal';
        if (resp.status >= 500 || resp.status === 0) return 'retry';
        return 'fatal';
      } catch (_) {
        return 'retry';
      } finally {
        _submitInFlight = false;
      }
    }

    async function processQueue() {
      if (_submitInFlight) return;
      const items = queueRead();
      const now = Date.now();
      let mutated = false;
      for (let i = 0; i < items.length; i++) {
        const it = items[i];
        if (it.payload.hook !== CONFIG.hook) continue;
        if (it.firstAttemptAt && (now - it.firstAttemptAt) > QUEUE_MAX_AGE_MS) {
          items.splice(i, 1); i--; mutated = true; continue;
        }
        if (it.attempts >= MAX_ATTEMPTS) { items.splice(i, 1); i--; mutated = true; continue; }
        if (now < it.nextAttemptAt) continue;
        it.attempts++;
        it.nextAttemptAt = it.firstAttemptAt + BACKOFF_MS[Math.min(it.attempts, MAX_ATTEMPTS - 1)];
        mutated = true;
        const status = await submitOnce(it.payload);
        if (status === 'ok' || status === 'duplicate' || status === 'gate' || status === 'fatal') {
          items.splice(i, 1); i--;
        }
      }
      if (mutated) queueWrite(items);
    }

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      const values = readForm();
      if (!validate(values)) {
        sendGa4('fit_index_validation_failed', {});
        return;
      }
      const payload = buildPayload(values);
      showToast('Your guide is on its way. Check your inbox shortly.');
      sendGa4('fit_index_submit', { idempotency_key: payload.idempotency_key });
      queueAdd(payload);
      submitOnce(payload).then(function (status) {
        if (status === 'ok' || status === 'duplicate') {
          queueRemove(payload.idempotency_key);
        } else if (status === 'gate' || status === 'fatal') {
          queueRemove(payload.idempotency_key);
          showToast('We had trouble sending your guide. Please try again shortly.');
        }
      });
      form.reset();
    });

    document.addEventListener('DOMContentLoaded', processQueue);
    setInterval(processQueue, 30e3);

    // ── Turnstile (shared script load across instances) ──────────────────────
    function ensureTurnstileScript() {
      if (!CONFIG.turnstileSiteKey) return Promise.resolve(false);
      if (window.turnstile) return Promise.resolve(true);
      return new Promise(function (resolve) {
        const s = document.createElement('script');
        s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
        s.async = true; s.defer = true;
        s.onload = function () { resolve(true); };
        s.onerror = function () { resolve(false); };
        document.head.appendChild(s);
      });
    }
    ensureTurnstileScript().then(function (loaded) {
      if (!loaded || !window.turnstile) return;
      const container = root.querySelector('.dpc-fitindex-capture__turnstile');
      if (!container) return;
      window.turnstile.render(container, {
        sitekey: CONFIG.turnstileSiteKey,
        theme: 'light',
        size: 'normal',
        callback: function (token) {
          const hidden = form.querySelector('input[name="cf-turnstile-response"]');
          if (hidden) hidden.value = token;
        },
        'error-callback': function () { _turnstileErrored = true; sendGa4('fit_index_turnstile_error', {}); },
        'expired-callback': function () {
          const hidden = form.querySelector('input[name="cf-turnstile-response"]');
          if (hidden) hidden.value = '';
        },
      });
    });

    root.dataset.dpcFitIndexLoaded = '1';
  });
})();
